import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  Check,
  ChevronRight,
  ClipboardList,
  ExternalLink,
  Mail,
  MapPin,
  Palette,
  Settings2,
  ShieldCheck,
  Sparkles,
  Zap,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Seo } from "@/components/seo/Seo";

const templateSkins = [
  {
    id: "clean",
    slug: "chiaro-locale",
    name: "Chiaro locale",
    tag: "Per imprese familiari",
    accent: "from-sky-500 to-cyan-400",
    bg: "bg-sky-50",
    text: "text-sky-950",
    previewName: "Laguna Clean",
    promise: "Pulizie appartamenti e uffici a Jesolo",
    image: "/assets/public-photos/optimized/pexels-matilda-wormwood-4097993.jpg",
  },
  {
    id: "premium",
    slug: "premium-hospitality",
    name: "Premium hospitality",
    tag: "Per turnover turistici",
    accent: "from-violet-500 to-fuchsia-400",
    bg: "bg-violet-50",
    text: "text-violet-950",
    previewName: "Mare Suite Service",
    promise: "Cambi ospite rapidi, calendario sempre aggiornato",
    image: "/assets/public-photos/optimized/pexels-olly-3772615.jpg",
  },
  {
    id: "industrial",
    slug: "operativo-diretto",
    name: "Operativo diretto",
    tag: "Per team strutturati",
    accent: "from-emerald-500 to-teal-400",
    bg: "bg-emerald-50",
    text: "text-emerald-950",
    previewName: "Nord Est Pulizie",
    promise: "Preventivi, prenotazioni e zone in un unico flusso",
    image: "/assets/public-photos/optimized/pexels-tima-miroshnichenko-6196677.jpg",
  },
] as const;

const backendSteps = [
  { icon: Palette, title: "Brand", text: "Logo, colori, nome azienda e tono." },
  { icon: ClipboardList, title: "Servizi", text: "Cambio ospite, fondo, uffici, post-cantiere." },
  { icon: MapPin, title: "Zone", text: "Citta servite, aree escluse e priorita locali." },
  { icon: CalendarDays, title: "Booking", text: "Richieste e disponibilita entrano nel backend Halomora." },
] as const;

const validationSignals = [
  "Il cliente vede il tuo brand, non Halomora come marketplace.",
  "I clienti restano tuoi: Halomora abilita il sistema.",
  "Ogni richiesta aiuta a capire capacita, zone e affidabilita.",
] as const;

export default function BusinessBookingSystemDemo() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [selectedSkin, setSelectedSkin] = useState<(typeof templateSkins)[number]["id"]>("premium");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const activeSkin = templateSkins.find((skin) => skin.id === selectedSkin) || templateSkins[1];

  const scrollToForm = () => {
    document.getElementById("demo-contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const openSelectedDemo = () => {
    navigate(`/for-businesses/booking-system-demo/${activeSkin.slug}`);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const payload = {
      company_name: String(formData.get("company_name") || ""),
      email: String(formData.get("email") || ""),
      phone: String(formData.get("phone") || ""),
      region: String(formData.get("region") || ""),
      message: String(formData.get("message") || ""),
      service_interest: `booking_system_demo:${selectedSkin}`,
      ref_code: "",
      landing_page: typeof window !== "undefined" ? window.location.href : "",
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      source: "booking-system-demo",
    };

    try {
      setSubmitting(true);
      const response = await fetch("/api/notifications?action=contact_sales", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.error || "Non siamo riusciti a inviare la richiesta.");
      }

      setSent(true);
      formRef.current?.reset();
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      toast({
        title: "Richiesta inviata",
        description: "Ti contatteremo per capire se il sistema e adatto alla tua azienda.",
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Riprova tra qualche minuto.";
      toast({
        title: "Invio non riuscito",
        description: message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen app-page bg-slate-50">
      <Seo
        title="Sito Web per Imprese di Pulizie con Booking Online | Halomora"
        description="Esempi di sito web professionale per imprese di pulizie con prenotazioni online, form preventivo, pagine servizi e zone. Il sito è collegato al backend Halomora."
        canonicalPath="/for-businesses/booking-system-demo"
      />

      <section className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_10%,rgba(56,189,248,0.28),transparent_32%),radial-gradient(circle_at_82%_22%,rgba(168,85,247,0.24),transparent_30%),linear-gradient(180deg,rgba(15,23,42,0.98),rgba(2,6,23,1))]" />
        <div className="relative container mx-auto max-w-7xl px-6 py-10 md:py-16">
          <div className="mb-8 flex justify-between gap-3">
            <Button
              variant="outline"
              className="border-white/20 bg-white/5 text-white hover:bg-white/12"
              onClick={() => navigate("/for-businesses")}
            >
              <ArrowLeft className="h-4 w-4" />
              Per aziende
            </Button>
            <Button className="hidden sm:inline-flex" onClick={scrollToForm}>
              Richiedi demo
            </Button>
          </div>

          <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div className="max-w-3xl">
              <Badge className="mb-5 border-white/15 bg-white text-slate-950 hover:bg-white">
                Sito professionale + backend Halomora
              </Badge>
              <h1 className="text-4xl font-bold leading-tight tracking-tight text-white md:text-6xl">
                Sito web per impresa di pulizie, già pronto per ricevere prenotazioni.
              </h1>
              <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-200 md:text-xl">
                Scegli uno stile, aggiungi il tuo brand e guarda come può apparire un sito completo per la tua azienda di pulizie:
                servizi, zone servite, preventivi e booking online collegati a Halomora.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Button size="lg" onClick={scrollToForm}>
                  Voglio vedere se fa per me
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/20 bg-white/5 text-white hover:bg-white/12"
                  onClick={() => window.open("https://jservicejesolo.com/", "_blank", "noopener,noreferrer")}
                >
                  Esempio reale: JService
                  <ExternalLink className="h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/20 bg-white/5 text-white hover:bg-white/12"
                  onClick={openSelectedDemo}
                >
                  Apri stile scelto
                  <Sparkles className="h-4 w-4" />
                </Button>
              </div>
              <p className="mt-5 text-sm text-slate-400">
                Non è un semplice template grafico: è il front-end commerciale della tua impresa, collegato a un flusso operativo.
              </p>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-sky-500/16 via-violet-500/14 to-emerald-400/14 blur-lg" />
              <div className="relative rounded-[2rem] border border-white/12 bg-white/8 p-3 shadow-xl">
                <div className="rounded-[1.5rem] bg-white p-4">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                        Anteprima sito + booking
                      </div>
                      <div className="text-xl font-bold text-slate-950">{activeSkin.previewName}</div>
                    </div>
                    <div className={`h-11 w-11 rounded-2xl bg-gradient-to-br ${activeSkin.accent}`} />
                  </div>
                  <div className={`overflow-hidden rounded-[1.25rem] ${activeSkin.bg}`}>
                    <div className="grid gap-0 md:grid-cols-[0.85fr_1.15fr]">
                      <div className="p-5">
                        <div className={`mb-3 inline-flex rounded-full bg-white/80 px-3 py-1 text-xs font-semibold ${activeSkin.text}`}>
                          Sito pronto all'uso
                        </div>
                        <h2 className={`text-2xl font-bold leading-tight ${activeSkin.text}`}>{activeSkin.promise}</h2>
                        <div className="mt-5 grid gap-2 text-sm text-slate-700">
                          <div className="rounded-xl bg-white/75 p-3">Pulizia appartamenti vacanze</div>
                          <div className="rounded-xl bg-white/75 p-3">Pulizia uffici e negozi</div>
                          <div className="rounded-xl bg-white/75 p-3">Sanificazione e post-cantiere</div>
                        </div>
                      </div>
                      <div className="m-3 rounded-[1rem] bg-white p-4 shadow-md">
                        <div className="text-sm font-semibold text-slate-950">Form integrato Halomora</div>
                        <div className="mt-3 space-y-2">
                          <div className="h-10 rounded-xl bg-slate-100" />
                          <div className="h-10 rounded-xl bg-slate-100" />
                          <div className="h-20 rounded-xl bg-slate-100" />
                          <div className={`h-10 rounded-xl bg-gradient-to-r ${activeSkin.accent}`} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-2 sm:grid-cols-3">
                    {templateSkins.map((skin) => (
                      <button
                        key={skin.id}
                        type="button"
                        onClick={() => setSelectedSkin(skin.id)}
                        className={`rounded-2xl border p-3 text-left transition-all ${
                          selectedSkin === skin.id
                            ? "border-slate-950 bg-slate-950 text-white"
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                        }`}
                      >
                        <div className={`mb-2 h-2 rounded-full bg-gradient-to-r ${skin.accent}`} />
                        <div className="text-sm font-semibold">{skin.name}</div>
                        <div className={selectedSkin === skin.id ? "text-xs text-slate-300" : "text-xs text-slate-500"}>
                          {skin.tag}
                        </div>
                        <div className={selectedSkin === skin.id ? "mt-2 text-xs text-white" : "mt-2 text-xs text-primary"}>
                          Clicca per selezionare
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="container mx-auto max-w-7xl px-6 py-12 md:py-16">
        <section className="grid gap-5 md:grid-cols-4">
          {backendSteps.map((step) => (
            <Card key={step.title} className="border-slate-200/80">
              <CardContent className="p-5">
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <step.icon className="h-5 w-5" />
                </div>
                <h2 className="text-lg font-semibold text-slate-950">{step.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.text}</p>
              </CardContent>
            </Card>
          ))}
        </section>

        <section className="mt-12 space-y-6">
          <div className="max-w-3xl">
            <Badge variant="outline">Esempi di sito completo</Badge>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">
              Tre design diversi, tutti già collegabili al backend.
            </h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Ogni demo segue una struttura simile a un sito reale per impresa di pulizie: servizi, zone servite, fiducia,
              preventivo, prenotazioni online e domande frequenti. Cambiano look, tono e priorità, ma il flusso Halomora resta integrato.
            </p>
          </div>
          <div className="grid gap-5 lg:grid-cols-3">
            {templateSkins.map((skin) => (
              <button
                key={skin.id}
                type="button"
                onClick={() => navigate(`/for-businesses/booking-system-demo/${skin.slug}`)}
                className="group overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white text-left shadow-card transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-medium"
              >
                <div className={`${skin.bg} p-5`}>
                  <div className={`mb-4 h-2 rounded-full bg-gradient-to-r ${skin.accent}`} />
                  <div className="rounded-2xl bg-white/82 p-4 shadow-sm">
                    <img
                      src={skin.image}
                      alt={skin.name}
                      loading="lazy"
                      decoding="async"
                      className="mb-4 h-36 w-full rounded-xl object-cover"
                    />
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className={`text-xs font-semibold uppercase tracking-[0.14em] ${skin.text}`}>
                          {skin.name}
                        </div>
                        <div className="mt-1 text-xl font-bold text-slate-950">{skin.previewName}</div>
                      </div>
                      <ExternalLink className="h-5 w-5 text-slate-400 transition-colors group-hover:text-primary" />
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{skin.promise}</p>
                    <div className="mt-4 grid gap-2 text-xs text-slate-600">
                      <div className="rounded-xl bg-slate-50 px-3 py-2">Homepage + pagine interne</div>
                      <div className="rounded-xl bg-slate-50 px-3 py-2">Servizi, zone, FAQ e SEO locale</div>
                      <div className="rounded-xl bg-slate-50 px-3 py-2">Booking e preventivi Halomora</div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between px-5 py-4 text-sm font-semibold text-primary">
                  <span>Apri sito demo navigabile</span>
                  <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="mt-12 grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div className="space-y-5">
            <Badge variant="outline">Perche non e solo un sito</Badge>
            <h2 className="text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">
              Tu lavori con i clienti. Halomora tiene in ordine il flusso.
            </h2>
            <p className="text-lg leading-relaxed text-slate-600">
              Il cliente compila una richiesta sul tuo sito. Tu ricevi dati puliti: servizio, zona, contatto,
              urgenza e note. Da li puoi gestire preventivo, disponibilita e prenotazione.
            </p>
            <div className="space-y-3">
              {validationSignals.map((signal) => (
                <div key={signal} className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2.6} />
                  <span>{signal}</span>
                </div>
              ))}
            </div>
          </div>

          <Card className="overflow-hidden border-slate-200/80">
            <CardContent className="p-0">
              <div className="grid gap-0 md:grid-cols-2">
                <div className="bg-slate-950 p-6 text-white">
                  <div className="mb-6 flex items-center gap-2 text-sm font-semibold text-emerald-300">
                    <ShieldCheck className="h-4 w-4" />
                    I clienti restano tuoi
                  </div>
                  <h3 className="text-2xl font-bold">Halomora lavora dietro le quinte.</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-300">
                    Il sito mostra il tuo brand. Halomora alimenta richieste, prenotazioni e dati operativi.
                  </p>
                </div>
                <div className="space-y-3 p-6">
                  {[
                    ["Cliente", "compila la richiesta sul tuo sito"],
                    ["Impresa", "riceve lead e dettagli ordinati"],
                    ["Halomora", "gestisce backend e flusso operativo"],
                  ].map(([label, text]) => (
                    <div key={label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-xs font-semibold uppercase tracking-[0.12em] text-primary">{label}</div>
                      <div className="mt-1 text-sm text-slate-700">{text}</div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="mt-12 grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-start" id="demo-contact">
          <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-emerald-500/5">
            <CardContent className="p-6 md:p-8">
              <Badge className="mb-4">Test pilota</Badge>
              <h2 className="text-3xl font-bold tracking-tight text-slate-950">Vuoi capire se puo funzionare per la tua impresa?</h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Scrivimi: ti ricontatto per capire zona, servizi, sito attuale e se ha senso attivare un primo
                booking system brandizzato.
              </p>
              <div className="mt-6 grid gap-3 text-sm">
                {[
                  ["Ideale per", "Imprese di pulizie in Veneto con servizi per appartamenti, uffici o turismo."],
                  ["Non serve", "Avere gia un sito moderno o un gestionale complesso."],
                  ["Obiettivo", "Mettere online un flusso chiaro, non fare un sito decorativo."],
                ].map(([label, text]) => (
                  <div key={label} className="rounded-2xl border border-white/80 bg-white/80 p-4">
                    <div className="font-semibold text-slate-950">{label}</div>
                    <div className="mt-1 text-slate-600">{text}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200/80">
            <CardContent className="p-6 md:p-8">
              <div className="mb-5 flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                <h2 className="text-2xl font-bold text-slate-950">Richiedi un contatto</h2>
              </div>
              {sent ? (
                <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" role="status">
                  Richiesta inviata. Ti contatteremo presto con i prossimi passi.
                </div>
              ) : null}
              <form ref={formRef} onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
                <Input name="company_name" placeholder="Nome azienda" required disabled={submitting} />
                <Input name="email" type="email" placeholder="Email" required disabled={submitting} />
                <Input name="phone" placeholder="Telefono / WhatsApp" disabled={submitting} />
                <Input name="region" placeholder="Citta o zona servita" disabled={submitting} />
                <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-950">
                    <Settings2 className="h-4 w-4 text-primary" />
                    Stile scelto
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {templateSkins.map((skin) => (
                      <button
                        key={skin.id}
                        type="button"
                        onClick={() => setSelectedSkin(skin.id)}
                        disabled={submitting}
                        className={`rounded-full border px-4 py-2 text-sm font-medium ${
                          selectedSkin === skin.id
                            ? "border-primary bg-primary text-white"
                            : "border-slate-200 bg-white text-slate-700 hover:border-primary/40"
                        }`}
                      >
                        {skin.name}
                      </button>
                    ))}
                  </div>
                </div>
                <Textarea
                  name="message"
                  rows={5}
                  className="md:col-span-2"
                  placeholder="Raccontami che servizi offri, in quali zone lavori e se hai gia un sito."
                  disabled={submitting}
                />
                <div className="md:col-span-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs leading-relaxed text-slate-500">
                    Demo interattiva: provala, scegli uno stile e richiedi un contatto per attivare il tuo sito con booking collegato a Halomora.
                  </p>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? "Invio..." : "Invia richiesta"}
                    <Zap className="h-4 w-4" />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
}
